import { addressesMatch } from '@/lib/address-match';
import { soldLineSet } from '@/lib/sales/service-components';
import { dateToDateString } from '@/lib/date-values';
import { billingAddressForCompare, serviceAddressForCompare } from '@/lib/jobs/jobAddressSources';
import { resolveEffectivePriceStrategy } from '@/lib/sales/pricingMode';
import { SUB_CONTACT_NONE } from '@/components/molecules/ContactPersonCard';
import { toTermsPayload, type TermsSelection } from '@/lib/terms';
import type { EditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type {
  AuthorizationLevel,
  AuthorizationMethod,
  BookingType,
  CreateJobDto,
  IntervalUnit,
  InvoicingMethod,
  InvoicingMode,
  InvoicingSchedule,
  JobAddress,
  JobPriority,
  JobType,
  RecurringEndType,
  RecurringDayConstraintMode,
  RecurringFrequency,
  RecurringManualSchedule,
  RecurringPatternMode,
  RecurringVisitGranularity,
  RecurringVisitPlacement,
  RecurringVisitPattern,
  RecurringWeatherPolicy,
  UpdateJobDto,
} from '@/types/job';
import type { AdditionalContact } from '@/types/contact';
import type { UnifiedRule } from '@/hooks/payment-schedules/usePaymentScheduleEditor';
import type { BillingCadenceFreq, BillingMode } from '@/lib/jobs/jobFormModel';

/** §6.2 — anniversary escalation rule type chosen on the job form. */
export type EscalationFormType =
  | 'NONE'
  | 'FIXED_PERCENT'
  | 'FIXED_AMOUNT'
  | 'CPI_INDEXED'
  | 'SCHEDULED_STEPS';

/** §6.1 — proration policy chosen on the job form. */
export type ProrationFormType = 'DAILY' | 'FULL_MONTH' | 'NONE';

/**
 * Advanced-section inherit tri-states. Each control is `'inherit'` (omit from the
 * DTO → the JobType default applies) or an explicit per-job override. Mirrors the
 * arrival-window `'inherit' | value` pattern so the create form can surface every
 * inherited default and let the user override it one field at a time.
 */
export type GateChoice = 'inherit' | 'required' | 'not_required';
export type AuthorizationLevelChoice = 'inherit' | AuthorizationLevel;
export type AuthorizationMethodChoice = 'inherit' | AuthorizationMethod;

export interface JobCreateFormValues {
  name: string;
  issueSummary: string;
  jobType: JobType;
  jobTypeId?: string;
  jobTypeName?: string;
  priority: JobPriority;
  assignedTo: string;
  salesRepId: string;
  pipelineId: string;
  stageId: string;
  // NOT-542 — Business Unit override. null = inherit from the job type; a
  // non-null value is a dispatcher's manual override.
  businessUnitId: string | null;
  // NOT-542 — set only on an estimate→job conversion draft, never by the
  // picker: the unit the source estimate chose, and which channel chose it.
  // `businessUnitId` above opens on this value; keeping the two comparable is
  // what tells a reviewer who left it alone from one who removed it. See
  // `estimateToJobFormValues`.
  carriedBusinessUnit?: { id: string; source: 'SELECTED' | 'MANUAL_OVERRIDE' } | null;
  contactId: string;
  billToContactId: string;
  billToId?: string | null;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  notes: string;
  scopeOfWork: string;
  instructions: string;
  internalNotes: string;
  // Terms & Conditions selection for this job. Seeded from the Job Type (or the
  // source estimate) and overridable per job.
  terms: TermsSelection;
  bookingType: BookingType;
  recurPatternMode: RecurringPatternMode;
  recurVisitsPerCycle: string;
  recurDayConstraintMode: RecurringDayConstraintMode;
  recurAllowedWeekDays: number[];
  recurFrequency: RecurringFrequency;
  recurStartDate: string;
  recurVisitStartTime: string;
  recurVisitEndTime: string;
  recurVisitEndOverride: boolean;
  recurVisitPatterns: RecurringVisitPattern[];
  recurVisitGranularity?: RecurringVisitGranularity;
  recurEndType: RecurringEndType;
  recurEndAfterCount: string;
  recurEndOnDate: string;
  recurInterval: string;
  recurIntervalUnit: IntervalUnit;
  recurWeekDays: number[];
  recurCustomDays: number[];
  recurMonthMode: 'dayOfMonth' | 'dayOfWeek';
  recurMonthDay: number | null;
  recurMonthLastDay: boolean;
  recurMonthDayOfWeek: number | null;
  recurMonthDayOfWeekOccurrence: number;
  // Recurring spec §3/§7 — billing mode (per visit / subscription / prepaid).
  billingMode: BillingMode;
  recurBaseAmount: string;
  recurCadence: BillingCadenceFreq;
  recurAnchorDay: string;
  recurTermMonths: string;
  recurEscalationType: EscalationFormType;
  recurEscalationValue: string;
  recurEscalationRuleId: string;
  recurProrationPolicy: ProrationFormType;
  // §3 / §8 / §17 — advanced recurrence overrides (season, holiday, contract terms).
  recurSeasonStart?: string;
  recurSeasonEnd?: string;
  recurHolidayPolicy?: 'SKIP' | 'NEXT_BUSINESS_DAY' | 'PROCEED';
  recurWeatherPolicy?: RecurringWeatherPolicy;
  recurAutoRenew?: boolean;
  recurPauseBillingPolicy?: 'CONTINUE' | 'PRORATE_CREDIT' | 'SUSPEND';
  recurCancellationFeePolicy?: 'none' | 'flat' | 'minimum_term_remaining';
  recurMinimumTermMonths?: number;
  recurCancellationFee?: number;
  invoicingMethod: InvoicingMethod;
  invoicingSchedule: InvoicingSchedule;
  invoicingMode: InvoicingMode;
  autoInvoiceOnComplete: boolean;
  autoCharge: boolean;
  autoChargePaymentMethodId: string;
  depositAmount: string;
  tagIds: string[];
  createdByRole: string;
  leadSource: string;
  reviewRequestEnabled: boolean;
  requiresCustomerApproval?: boolean;
  // NOT — Advanced-section inherit tri-states. `'inherit'` (or an empty cap string)
  // omits the field from the DTO so the JobType default applies; an explicit value
  // overrides that one field for this job only.
  workAuthorization: GateChoice;
  authorizationLevel: AuthorizationLevelChoice;
  authorizationMethod: AuthorizationMethodChoice;
  authorizationMaxAmount: string;
  completionSignoff: GateChoice;
  signoffLevel: AuthorizationLevelChoice;
  visitCancellationWindowHours: string;
  visitCancellationFeeType: 'NONE' | 'FLAT' | 'PERCENT';
  visitCancellationFeeAmount: string;
  visitDepositRefundable: boolean;
}

export interface JobEditFormValues {
  name: string;
  jobType: JobType;
  priority: JobPriority;
  assignedTo: string;
  salesRepId: string;
  pipelineId: string;
  // NOT-542 — Business Unit override (see JobCreateFormValues).
  businessUnitId: string | null;
  billToContactId: string;
  billToId?: string | null;
  tagIds: string[];
  notes: string;
  scopeOfWork: string;
  instructions: string;
  internalNotes: string;
  // Terms & Conditions selection for this job. Seeded from the Job Type (or the
  // source estimate) and overridable per job.
  terms: TermsSelection;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  bookingType: BookingType;
  recurPatternMode: RecurringPatternMode;
  recurVisitsPerCycle: string;
  recurDayConstraintMode: RecurringDayConstraintMode;
  recurAllowedWeekDays: number[];
  recurFrequency: RecurringFrequency;
  recurStartDate: string;
  recurVisitStartTime: string;
  recurVisitEndTime: string;
  recurVisitEndOverride: boolean;
  recurVisitPatterns: RecurringVisitPattern[];
  recurEndType: RecurringEndType;
  recurEndAfterCount: string;
  recurEndOnDate: string;
  recurInterval: string;
  recurIntervalUnit: IntervalUnit;
  recurWeekDays: number[];
  recurCustomDays: number[];
  recurMonthMode: 'dayOfMonth' | 'dayOfWeek';
  recurMonthDay: number | null;
  recurMonthLastDay: boolean;
  recurMonthDayOfWeek: number | null;
  recurMonthDayOfWeekOccurrence: number;
  // Recurring spec §3/§7 — billing mode (per visit / subscription / prepaid).
  billingMode: BillingMode;
  recurBaseAmount: string;
  recurCadence: BillingCadenceFreq;
  recurAnchorDay: string;
  recurTermMonths: string;
  recurEscalationType: EscalationFormType;
  recurEscalationValue: string;
  recurEscalationRuleId: string;
  recurProrationPolicy: ProrationFormType;
  // §3 / §8 / §17 — advanced recurrence overrides (season, holiday, contract terms).
  recurSeasonStart?: string;
  recurSeasonEnd?: string;
  recurHolidayPolicy?: 'SKIP' | 'NEXT_BUSINESS_DAY' | 'PROCEED';
  recurWeatherPolicy?: RecurringWeatherPolicy;
  recurAutoRenew?: boolean;
  recurPauseBillingPolicy?: 'CONTINUE' | 'PRORATE_CREDIT' | 'SUSPEND';
  recurCancellationFeePolicy?: 'none' | 'flat' | 'minimum_term_remaining';
  recurMinimumTermMonths?: number;
  recurCancellationFee?: number;
  invoicingMethod: InvoicingMethod;
  invoicingSchedule: InvoicingSchedule;
  invoicingMode: InvoicingMode;
  autoInvoiceOnComplete: boolean;
  autoCharge: boolean;
  autoChargePaymentMethodId: string;
  depositAmount: string;
  leadSource: string;
  createdByRole: string;
  reviewRequestEnabled: boolean;
  // NOT-296 — job-level authorization / sign-off overrides, now editable on the edit form.
  workAuthorization: GateChoice;
  authorizationLevel: AuthorizationLevelChoice;
  authorizationMethod: AuthorizationMethodChoice;
  authorizationMaxAmount: string;
  completionSignoff: GateChoice;
  signoffLevel: AuthorizationLevelChoice;
  visitCancellationWindowHours: string;
  visitCancellationFeeType: 'NONE' | 'FLAT' | 'PERCENT';
  visitCancellationFeeAmount: string;
  visitDepositRefundable: boolean;
}

interface JobCustomerAddressLike {
  id?: string;
  street?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  postalCode?: string | null;
  country?: string | null;
  // Address-level tax-exemption snapshot fields, present on saved contact
  // addresses. Copied onto the SERVICE address at job-creation time.
  isTaxExempt?: boolean;
  taxExemptCertificate?: string | null;
  taxExemptExpiry?: string | null;
}

export interface JobCustomerPayloadState {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  contactAddresses: JobCustomerAddressLike[];
  billToId?: string | null;
  hasCatalogBillTo?: boolean;
  additionalContacts?: AdditionalContact[];
  // Edit-only: whether the loaded job already had a billing-address / service-contact
  // snapshot. Lets buildUpdateJobDto send an explicit `null` to CLEAR when the user
  // removes a previously saved value — omitting it would leave the old snapshot behind,
  // since JobsService.update only writes these fields when the property is present.
  initialHadBillingAddress?: boolean;
  initialHadServiceContact?: boolean;
  /** Edit-only: the billing address the job was saved with. */
  initialBillingAddress?: JobCustomerAddressLike | null;
}

function trimOrUndefined(value: string | null | undefined) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function normalizeAddress(
  address: JobCustomerAddressLike,
  // When true, copy the address's tax-exemption snapshot onto the result. Only
  // the SERVICE address sourced from a saved ContactAddress sets this.
  includeTaxSnapshot = false,
): JobAddress | undefined {
  const street = trimOrUndefined(address.street ?? address.street1);
  const city = trimOrUndefined(address.city);
  const state = trimOrUndefined(address.state);
  const zip = trimOrUndefined(address.zip ?? address.postalCode);
  if (!street || !city || !state || !zip) return undefined;

  return {
    street,
    ...(trimOrUndefined(address.street2) ? { street2: trimOrUndefined(address.street2) } : {}),
    city,
    state,
    zip,
    country: trimOrUndefined(address.country) ?? 'US',
    ...(address.id ? { addressId: address.id } : {}),
    ...(includeTaxSnapshot
      ? {
          isTaxExempt: address.isTaxExempt ?? false,
          taxExemptCertificate: address.taxExemptCertificate ?? null,
          taxExemptExpiry: address.taxExemptExpiry ?? null,
        }
      : {}),
  };
}

function manualAddress({
  street,
  street2,
  city,
  state,
  zip,
}: {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}): JobAddress | undefined {
  const normalized = normalizeAddress({
    street,
    street2,
    city,
    state,
    zip,
    country: 'US',
  });
  return normalized;
}

function buildServiceAddressFromCustomerState(
  customerState: JobCustomerPayloadState | undefined,
  fallback: Pick<JobCreateFormValues, 'street' | 'street2' | 'city' | 'state' | 'zip' | 'country'>,
) {
  if (!customerState) {
    const hasAddress = fallback.street || fallback.city || fallback.state || fallback.zip;
    if (!hasAddress) return undefined;
    return normalizeAddress(fallback);
  }

  if (customerState.addressMode === 'manual') {
    return manualAddress({
      street: customerState.saStreet,
      street2: customerState.saStreet2,
      city: customerState.saCity,
      state: customerState.saState,
      zip: customerState.saZip,
    });
  }

  if (customerState.selectedAddressIndex === null) {
    // Inline “New customer” creation writes the address fields immediately,
    // then creates/selects the contact during Create Job. React has not yet
    // rendered that selection here, so the mode is still `select` with no
    // saved-address index even though the complete typed address is present.
    // Preserve that snapshot on the job; otherwise its first visit, booking
    // confirmation, and Maps link all inherit an empty address.
    const transientAddress = serviceAddressForCompare(customerState);
    return transientAddress ? normalizeAddress(transientAddress) : undefined;
  }
  const address = customerState.contactAddresses[customerState.selectedAddressIndex];
  // Saved service address → snapshot its tax-exemption status onto the job.
  return address ? normalizeAddress(address, true) : undefined;
}

function buildBillingAddressFromCustomerState(customerState: JobCustomerPayloadState | undefined) {
  if (!customerState || customerState.hasCatalogBillTo) return undefined;

  // Omitting the key IS how a job records "billing is the same as service", so
  // this comparison decides whether a redundant copy gets persisted.
  //
  // It compares the values the user SUPPLIED — the same operands
  // `JobCustomerContactsSection` warns from, via the same two selectors — and
  // not the built `JobAddress` pair. `normalizeAddress` and `manualAddress`
  // stamp `country: 'US'` on what they build, so comparing those called an
  // identical billing address on a Canadian service address "different" and
  // persisted it, contradicting the form. See lib/jobs/jobAddressSources.ts.
  const selectedBilling =
    customerState.billingMode === 'manual' || customerState.selectedBillingIndex === null
      ? null
      : customerState.contactAddresses[customerState.selectedBillingIndex];
  const selectedService =
    customerState.addressMode === 'manual' || customerState.selectedAddressIndex === null
      ? null
      : customerState.contactAddresses[customerState.selectedAddressIndex];
  if (
    (!selectedBilling?.id || !selectedService?.id || selectedBilling.id === selectedService.id) &&
    addressesMatch(billingAddressForCompare(customerState), serviceAddressForCompare(customerState))
  ) {
    return undefined;
  }

  if (customerState.billingMode === 'manual') {
    return manualAddress({
      street: customerState.baStreet,
      street2: customerState.baStreet2,
      city: customerState.baCity,
      state: customerState.baState,
      zip: customerState.baZip,
    });
  }

  if (customerState.selectedBillingIndex === null) return undefined;
  const address = customerState.contactAddresses[customerState.selectedBillingIndex];
  return address ? normalizeAddress(address) : undefined;
}

/**
 * A job saved with billing AT its service address reopens as "same as
 * service" — the form has no other way to show it — and saving it untouched
 * must not read as the user clearing it. Cleared, the job would bill the
 * customer's default billing site, a different place from the one on screen.
 * Only a stored billing address that differs from the service address (the user
 * reset it to service) is cleared, and a catalog bill-to still replaces it.
 */
function keepsStoredBillingAtService(customerState: JobCustomerPayloadState): boolean {
  const stored = customerState.initialBillingAddress;
  if (!stored || customerState.hasCatalogBillTo || customerState.billingMode === 'manual') {
    return false;
  }
  const service = serviceAddressForCompare(customerState);
  return Boolean(service) && addressesMatch(stored, service!);
}

function buildServiceContact(customerState?: JobCustomerPayloadState) {
  if (!customerState) return undefined;
  const matchedSubContact = customerState.additionalContacts?.find(
    (contact) =>
      (contact.email && contact.email === customerState.customerEmail) ||
      (contact.phone && contact.phone === customerState.customerPhone),
  );
  const name =
    trimOrUndefined(matchedSubContact?.name) ?? trimOrUndefined(customerState.customerName);
  if (!name) return undefined;

  return {
    name,
    email: trimOrUndefined(customerState.customerEmail),
    phone: trimOrUndefined(customerState.customerPhone),
  };
}

export function buildJobLineItems(
  lineItems: EditableLineItem[] | undefined,
  scopePricingMode: 'STANDARD' | 'TM_OVERRIDE',
  options?: {
    // Drop unselected optional rows. The backend bills every line it receives
    // (it forces isSelected=true on create), so a declined add-on must not be
    // sent. Off by default — the job-type template payload keeps unselected
    // optional rows, which are configuration for later selection.
    dropUnselectedOptional?: boolean;
    // Persist the editor's computed per-line totals. Some consumers (the
    // online-booking price fallback) read the snapshot with no server pricing
    // engine, so T&M / cost-plus lines must carry their computed lineTotal or
    // they'd be mispriced as basePrice * quantity.
    computePreview?: (
      row: EditableLineItem,
      mode: 'STANDARD' | 'TM_OVERRIDE',
    ) => { lineSubtotal: number; lineTotal: number };
  },
): Array<Record<string, unknown>> | undefined {
  // Drop blank custom rows the user added but never named — the backend
  // JobLineItemDto requires a non-empty name and would 400 on them.
  //
  // A dropped optional service takes its parts with it: left behind, they
  // arrive at the server with a parent that is not there, and an ORPHANED part
  // stands on its own — priced in full — while the editor's preview had
  // already taken it out of the sale with its service (`soldLines`).
  const named = (lineItems ?? []).filter((row) => row.name.trim().length > 0);
  const sold = options?.dropUnselectedOptional ? soldLineSet(named) : null;
  const rows = named.filter((row) => {
    if (!sold) return true;
    if (sold.has(row)) return true;
    // A non-optional part under a dropped service is out of the sale; an
    // optional line the operator merely unticked is dropped as before.
    return false;
  });
  if (rows.length === 0) return undefined;

  return rows.map((row, index) => {
    // The row's own answer first. A RELOADED custom line has a `lineItemId` —
    // the payload below backfills one from `_rowId`, because the backend
    // requires it — so deriving custom-ness from its absence un-marked every
    // hand-typed line the moment the job was opened and saved again, writing
    // it back as `pricebook` and handing it to the T&M rewrite.
    const isCustom = row.isCustom ?? !row.lineItemId;
    const isCustomPart =
      isCustom && ['MATERIAL', 'EQUIPMENT'].includes(String(row.type ?? '').toUpperCase());
    const priceStrategy =
      scopePricingMode === 'TM_OVERRIDE'
        ? resolveEffectivePriceStrategy(
            { type: row.type, priceStrategy: row.priceStrategy, isCustom },
            'TM_OVERRIDE',
          )
        : row.priceStrategy;
    const preview = options?.computePreview?.(row, scopePricingMode);

    return {
      snapshotId: row._rowId,
      // Backend requires a non-empty lineItemId + itemVersion >= 1 even for custom rows.
      lineItemId: row.lineItemId || row._rowId,
      itemVersion: row.itemVersion || 1,
      name: row.name,
      code: isCustom ? 'CUSTOM' : row.code || 'CUSTOM',
      type: row.type,
      unit: row.unit,
      ...(row.description ? { description: row.description } : {}),
      basePrice: row.basePrice,
      laborRate: row.laborRate,
      costBasis: row.costBasis,
      // A custom part may record either an unlinked direct cost or the supplier
      // whose quoted amount `costBasis` snapshots. Keep one effective number so
      // markup/margin cannot disagree with a duplicate "supplier cost" field.
      ...(isCustomPart
        ? {
            costSource: row.costSource === 'SUPPLIER' ? 'SUPPLIER' : 'DIRECT',
            ...(row.costSource === 'SUPPLIER' && row.costSupplierId
              ? {
                  costSupplierId: row.costSupplierId,
                  ...(row.costSupplierName ? { costSupplierName: row.costSupplierName } : {}),
                }
              : {}),
          }
        : {}),
      markupPercent: row.markupPercent,
      // What this line CHARGES, which is not the same question as what it is
      // worth. A part recovered through the service that consumes it prices at
      // zero, and the editor's own preview has always read these — dropping
      // them from the payload made the saved job cost more than the job the
      // dispatcher was looking at, which is the one disagreement a document
      // must never have with itself. Sent on every line, not only when set: an
      // override the payload omits is an override that reverts on the next
      // save. (`markupOverride` and `hiddenFromCustomer` stay conditional —
      // absent means "no override" and "shown", which are their defaults.)
      chargeable: row.chargeable !== false,
      pricingTreatment: row.pricingTreatment ?? null,
      ...(row.markupOverride != null ? { markupOverride: row.markupOverride } : {}),
      ...(row.hiddenFromCustomer ? { hiddenFromCustomer: true } : {}),
      // The CANONICAL spelling, stated on every line and derived from the one
      // flag the editor holds. Sending only `hiddenFromCustomer` leaves this one
      // stale on a line that already carried it — and `updateJobItem` merges
      // over the stored line, so a stale `visibleToCustomer: false` outlives the
      // operator turning the eye back on and the line never prints again. Money
      // is blind to visibility; what the customer sees is not.
      visibleToCustomer: row.hiddenFromCustomer !== true,
      // NOT-605 — carry the item-level member pricing + in-cart membership plan
      // snapshot so a reloaded job reapplies member rates in the scope preview.
      memberPrice: row.memberPrice ?? null,
      memberDiscountPercent: row.memberDiscountPercent ?? null,
      ...(row.membershipPlanId ? { membershipPlanId: row.membershipPlanId } : {}),
      // The billing option picked for a membership sold on the job: the
      // pending membership the job creates is sold on it, not on the plan's
      // default.
      ...(row.membershipBillingOptionId
        ? { membershipBillingOptionId: row.membershipBillingOptionId }
        : {}),
      priceStrategy,
      taxable: row.taxable,
      quantity: row.quantity,
      ...(row.discountAmount != null ? { discountAmount: row.discountAmount } : {}),
      ...(row.discountPercent != null ? { discountPercent: row.discountPercent } : {}),
      isOptional: row.isOptional,
      ...(row.isServiceCallFee !== undefined ? { isServiceCallFee: row.isServiceCallFee } : {}),
      isSelected: row.isSelected,
      source: isCustom ? 'custom' : 'pricebook',
      sortOrder: row.sortOrder ?? index,
      ...(typeof row.estimatedHours === 'number' ? { estimatedHours: row.estimatedHours } : {}),
      // What an hour COSTS, and the rows it was reduced from. `laborRate` above
      // is what an hour SELLS for and is not a substitute: the invoice write
      // path back-fills that one from the unit price, so costing from it reports
      // cost = price. The estimate payload has always sent both; the job's did
      // not, so a converted job costed its labor from `costBasis` — which is 0
      // on a dynamically-priced service — and reported the labor as free.
      ...(typeof row.laborCostRate === 'number' ? { laborCostRate: row.laborCostRate } : {}),
      ...(row.laborCostLines?.length ? { laborCostLines: row.laborCostLines } : {}),
      // The operator's typed price. Omitting it reverted the override to the
      // catalog base on the next save.
      ...(row.manualPriceOverride != null ? { manualPriceOverride: row.manualPriceOverride } : {}),
      ...(row.dynamicPricingConfig ? { dynamicPricingConfig: row.dynamicPricingConfig } : {}),
      ...(row.imageUrl ? { imageUrl: row.imageUrl } : {}),
      ...(row.imagePreviewUrl ? { imagePreviewUrl: row.imagePreviewUrl } : {}),
      ...(row.lineKey ? { lineKey: row.lineKey } : {}),
      ...(row.parentLineKey ? { parentLineKey: row.parentLineKey } : {}),
      ...(row.componentDisplayMode ? { componentDisplayMode: row.componentDisplayMode } : {}),
      ...(row.configurationName ? { configurationName: row.configurationName } : {}),
      ...(row.equipmentIds?.length ? { equipmentIds: row.equipmentIds } : {}),
      ...(row.equipmentLocationOverrideReason?.trim()
        ? { equipmentLocationOverrideReason: row.equipmentLocationOverrideReason.trim() }
        : {}),
      ...(preview ? { lineSubtotal: preview.lineSubtotal, lineTotal: preview.lineTotal } : {}),
    };
  });
}

// NOT-296: shape a UnifiedRule for the backend CreatePaymentScheduleRuleDto,
// which has NO milestoneRef field. Only include defined fields.
function mapRuleToDto(rule: UnifiedRule): Record<string, unknown> {
  const out: Record<string, unknown> = {
    trigger: rule.trigger,
    action: rule.action,
    amountSpec: rule.amountSpec,
    autoCharge: rule.autoCharge,
  };
  if (rule.paymentMethodId != null) out.paymentMethodId = rule.paymentMethodId;
  if (rule.daysAfter !== undefined) out.daysAfter = rule.daysAfter;
  if (rule.fixedDate !== undefined) out.fixedDate = rule.fixedDate;
  return out;
}

// §6.2 (H1) — inline anniversary escalation, attached regardless of billing
// mode (per-visit scales line prices; sub/prepaid bump the base). NONE → {} so
// the payload stays unchanged when no escalation is configured.
function buildEscalationConfig(
  form: {
    recurEscalationType: EscalationFormType;
    recurEscalationValue: string;
    recurEscalationRuleId: string;
    recurProrationPolicy: ProrationFormType;
  },
  options?: { clearEscalation?: boolean },
): {
  escalation?: {
    type: EscalationFormType;
    ruleId?: string;
    value?: number;
  };
} {
  // NOT-410 — a saved reusable schedule (picker) links by id; else an inline
  // custom rule; else nothing. Picker wins over inline.
  if (form.recurEscalationRuleId) {
    return {
      escalation: { type: 'NONE', ruleId: form.recurEscalationRuleId },
    };
  }
  if (!form.recurEscalationType || form.recurEscalationType === 'NONE') {
    if (options?.clearEscalation) {
      return { escalation: { type: 'NONE' } };
    }
    return {};
  }
  const value = Number(form.recurEscalationValue);
  return {
    escalation: {
      type: form.recurEscalationType,
      ...(Number.isFinite(value) && value > 0 ? { value } : {}),
    },
  };
}

// §3 / §8 / §17 — the "Advanced recurrence" slice of recurringConfig, shared by
// create + update. Season/holiday feed the visit generator; the contract-term
// fields (auto-renew, pause + cancellation-fee policy, minimum term, flat fee)
// fall back to tenant defaults when omitted, so each key is only sent when the
// user actually set it (empty payload stays byte-identical to pre-feature).
function buildRecurAdvancedConfig(form: {
  recurEndType: RecurringEndType;
  recurTermMonths: string;
  recurSeasonStart?: string;
  recurSeasonEnd?: string;
  recurHolidayPolicy?: 'SKIP' | 'NEXT_BUSINESS_DAY' | 'PROCEED';
  recurWeatherPolicy?: RecurringWeatherPolicy;
  recurAutoRenew?: boolean;
  recurPauseBillingPolicy?: 'CONTINUE' | 'PRORATE_CREDIT' | 'SUSPEND';
  recurCancellationFeePolicy?: 'none' | 'flat' | 'minimum_term_remaining';
  recurMinimumTermMonths?: number;
  recurCancellationFee?: number;
}) {
  // Emit an explicit null (not an omitted key) for every unset override so that
  // clearing one back to "Company default" on edit actually reaches the backend
  // and re-derives the tenant default — an omitted key is filtered out server-side
  // and would leave the previous override in place (a no-op clear). On create, null
  // resolves to the same default as omitting, so create output is unchanged.
  const hasFixedTerm =
    form.recurEndType === 'ON_DATE' ||
    form.recurEndType === 'AFTER_COUNT' ||
    Number(form.recurTermMonths) > 0;
  return {
    seasonStart: form.recurSeasonStart || null,
    seasonEnd: form.recurSeasonEnd || null,
    holidayPolicy: form.recurHolidayPolicy || null,
    weatherPolicy: form.recurWeatherPolicy || null,
    autoRenew: hasFixedTerm ? (form.recurAutoRenew ?? null) : null,
    pauseBillingPolicy: form.recurPauseBillingPolicy || null,
    cancellationFeePolicy: form.recurCancellationFeePolicy || null,
    minimumTermMonths: form.recurMinimumTermMonths ?? null,
    cancellationFee: form.recurCancellationFee ?? null,
  };
}

// Recurring spec §3/§7 — the billing-mode slice of recurringConfig, shared by
// create + update. PER_VISIT returns {} so its payload stays byte-identical to
// pre-feature (the backend defaults billingMode to PER_VISIT).
function buildBillingModeConfig(form: {
  billingMode: BillingMode;
  recurBaseAmount: string;
  recurCadence: BillingCadenceFreq;
  recurAnchorDay: string;
  recurTermMonths: string;
  recurEscalationType: EscalationFormType;
  recurEscalationValue: string;
  recurEscalationRuleId: string;
  recurProrationPolicy: ProrationFormType;
}) {
  const baseAmount = Number(form.recurBaseAmount);
  // §6.1 (M2) — proration applies to sub/prepaid mid-period math. The edit form
  // hydrates it truthfully from the persisted PricingTerms, so sending it on update
  // rewrites the same value (or the user's change) instead of clobbering it.
  const proration = { prorationPolicy: form.recurProrationPolicy };
  if (form.billingMode === 'SUBSCRIPTION') {
    const anchor = Number(form.recurAnchorDay);
    return {
      billingMode: 'SUBSCRIPTION' as const,
      ...(baseAmount > 0 ? { baseAmount } : {}),
      billingCadence: { freq: form.recurCadence, interval: 1 },
      // 1..31: a day past the end of a month bills on its last day.
      ...(Number.isFinite(anchor) && anchor >= 1
        ? { billingAnchorDay: Math.min(31, Math.max(1, Math.trunc(anchor))) }
        : {}),
      ...proration,
    };
  }
  if (form.billingMode === 'PREPAID') {
    const termMonths = Number(form.recurTermMonths);
    return {
      billingMode: 'PREPAID' as const,
      ...(baseAmount > 0 ? { baseAmount } : {}),
      ...(termMonths > 0 ? { termMonths: Math.trunc(termMonths) } : {}),
      ...proration,
    };
  }
  return {};
}

function recurringPatternKindsForForm(
  form: Pick<JobCreateFormValues, 'recurFrequency' | 'recurIntervalUnit'>,
): Array<NonNullable<JobCreateFormValues['recurVisitPatterns'][number]['kind']>> {
  const unit =
    form.recurFrequency === 'CUSTOM'
      ? form.recurIntervalUnit
      : form.recurFrequency === 'DAILY'
        ? 'DAYS'
        : form.recurFrequency === 'MONTHLY' ||
            form.recurFrequency === 'QUARTERLY' ||
            form.recurFrequency === 'SEMIANNUAL'
          ? 'MONTHS'
          : form.recurFrequency === 'ANNUAL'
            ? 'YEARS'
            : 'WEEKS';
  if (unit === 'DAYS') return ['DAILY_TIME'];
  if (unit === 'MONTHS') return ['MONTH_DAY', 'MONTH_WEEKDAY'];
  if (unit === 'YEARS') return ['YEAR_DATE'];
  return ['WEEKDAY'];
}

function resolveRecurringPlacementSettings(
  form: Pick<JobCreateFormValues, 'recurPatternMode'>,
  visitGranularity?: RecurringVisitGranularity,
  placement?: RecurringVisitPlacement,
) {
  const effectivePlacement =
    placement ??
    (form.recurPatternMode === 'FLEXIBLE'
      ? 'ROUTE_BASED'
      : visitGranularity === 'DAY_FLEXIBLE'
        ? 'ALL_DAY'
        : undefined);
  const effectiveVisitGranularity =
    effectivePlacement === 'SCHEDULE_NOW'
      ? 'TIMED'
      : effectivePlacement === 'ALL_DAY' || effectivePlacement === 'ROUTE_BASED'
        ? 'DAY_FLEXIBLE'
        : visitGranularity;

  return {
    effectivePlacement,
    effectiveVisitGranularity,
    recurringTimeTbd: effectiveVisitGranularity === 'DAY_FLEXIBLE',
  };
}

export function buildRecurringConfig(
  form: JobCreateFormValues,
  blackoutDates?: string[],
  visitGranularity?: RecurringVisitGranularity,
  placement?: RecurringVisitPlacement,
  /** Scheduling v2 — a series the office schedules by hand. */
  manualSchedule?: RecurringManualSchedule,
): NonNullable<CreateJobDto['recurringConfig']> {
  const recurInterval = Number(form.recurInterval);
  const isMonthly = form.recurFrequency === 'MONTHLY';
  const flexiblePattern = form.recurPatternMode === 'FLEXIBLE';
  const { effectivePlacement, effectiveVisitGranularity, recurringTimeTbd } =
    resolveRecurringPlacementSettings(form, visitGranularity, placement);
  const allowedPatternKinds = recurringPatternKindsForForm(form);
  const visitPatterns = form.recurVisitPatterns
    .filter(
      (pattern) =>
        Boolean(pattern.startTime) &&
        Boolean(pattern.endTime) &&
        allowedPatternKinds.includes(pattern.kind ?? 'WEEKDAY'),
    )
    // A manual series assigns the same technicians to every visit.
    .map((pattern) => {
      if (!manualSchedule) return pattern;
      const { technicianId: _technicianId, ...rest } = pattern;
      return rest;
    });
  const firstPattern = visitPatterns[0];
  const patternDays = Array.from(
    new Set(
      visitPatterns
        .filter((pattern) => pattern.kind === 'WEEKDAY' && pattern.dayOfWeek != null)
        .map((pattern) => pattern.dayOfWeek!),
    ),
  );
  return {
    frequency: form.recurFrequency,
    patternMode: form.recurPatternMode,
    ...(form.recurPatternMode === 'FLEXIBLE'
      ? {
          visitsPerCycle: Math.max(1, Number(form.recurVisitsPerCycle) || 1),
          dayConstraintMode: form.recurDayConstraintMode,
          allowedWeekDays: form.recurDayConstraintMode === 'ANY' ? [] : form.recurAllowedWeekDays,
        }
      : {}),
    startDate: form.recurStartDate || dateToDateString(new Date()),
    ...(effectiveVisitGranularity ? { visitGranularity: effectiveVisitGranularity } : {}),
    ...(effectivePlacement ? { placement: effectivePlacement } : {}),
    ...(!recurringTimeTbd && (firstPattern?.startTime || form.recurVisitStartTime)
      ? { visitStartTime: firstPattern?.startTime ?? form.recurVisitStartTime }
      : {}),
    ...(!recurringTimeTbd &&
    (firstPattern?.endTime || (form.recurVisitEndOverride && form.recurVisitEndTime))
      ? { visitEndTime: firstPattern?.endTime ?? form.recurVisitEndTime }
      : {}),
    ...(!flexiblePattern && visitPatterns.length ? { visitPatterns } : {}),
    endType: form.recurEndType,
    ...(form.recurEndType === 'AFTER_COUNT' && form.recurEndAfterCount
      ? { endAfterCount: Number(form.recurEndAfterCount) }
      : {}),
    ...(form.recurEndType === 'ON_DATE' && form.recurEndOnDate
      ? { endOnDate: form.recurEndOnDate }
      : {}),
    ...(recurInterval > 1 ? { interval: recurInterval } : {}),
    ...(!flexiblePattern &&
    (form.recurFrequency === 'WEEKLY' || form.recurFrequency === 'BIWEEKLY') &&
    (patternDays.length > 0 || form.recurWeekDays.length > 0)
      ? { customDays: patternDays.length > 0 ? patternDays : form.recurWeekDays }
      : {}),
    ...(form.recurFrequency === 'CUSTOM' ? { intervalUnit: form.recurIntervalUnit } : {}),
    ...(!flexiblePattern &&
    form.recurFrequency === 'CUSTOM' &&
    (patternDays.length > 0 || form.recurCustomDays.length > 0)
      ? { customDays: patternDays.length > 0 ? patternDays : form.recurCustomDays }
      : {}),
    ...(!flexiblePattern &&
    isMonthly &&
    (form.recurMonthMode ?? 'dayOfMonth') === 'dayOfMonth' &&
    (form.recurMonthLastDay || form.recurMonthDay !== null)
      ? {
          monthMode: 'dayOfMonth' as const,
          monthDay: form.recurMonthLastDay ? 32 : (form.recurMonthDay as number),
        }
      : {}),
    ...(!flexiblePattern &&
    isMonthly &&
    form.recurMonthMode === 'dayOfWeek' &&
    form.recurMonthDayOfWeek !== null
      ? {
          monthMode: 'dayOfWeek' as const,
          monthDayOfWeek: form.recurMonthDayOfWeek,
          monthDayOfWeekOccurrence: form.recurMonthDayOfWeekOccurrence,
        }
      : {}),
    ...buildBillingModeConfig(form),
    ...buildEscalationConfig(form),
    ...(blackoutDates?.length ? { blackoutDates } : {}),
    ...buildRecurAdvancedConfig(form),
    ...(manualSchedule ? { manualSchedule } : {}),
  };
}

export function buildCreateJobDto({
  form,
  customerState,
  tagIdsAuthoritative = false,
  selectedJobTypeId,
  selectedJobTypeValue,
  pendingAttachmentId,
  attachmentCount,
  lineItems,
  equipmentIds,
  pricingMode = 'STANDARD',
  scheduleRules,
  discountValue = 0,
  discountType = '$',
  discountReason = '',
  promotionId = null,
  depositValue = 0,
  depositType = '$',
  depositRequired = false,
  membershipPlanSnapshot,
  subtotal,
  taxTotal,
  discountTotal,
  membershipDiscountTotal,
  grandTotal,
  poNumber,
  blackoutDates,
  crew,
  crewId,
  routeMembership,
  routeMemberships,
  recurringVisitGranularity,
  recurringVisitPlacement,
  recurringManualSchedule,
  derivedBusinessUnitId = null,
  isCallback = false,
  callbackSourceJobId = null,
  sourceCallControlId,
}: {
  form: JobCreateFormValues;
  customerState?: JobCustomerPayloadState;
  /** True when the tag picker was seeded from a loaded job-scope catalog —
   * its state (including []) is then sent verbatim; otherwise the key is
   * omitted so the server inherits the job type's stamp. */
  tagIdsAuthoritative?: boolean;
  selectedJobTypeId?: string;
  selectedJobTypeValue: JobType;
  pendingAttachmentId?: string;
  attachmentCount?: number;
  lineItems?: EditableLineItem[];
  /** Existing customer equipment picked on the create form (job-level links). */
  equipmentIds?: string[];
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  scheduleRules?: UnifiedRule[];
  // NOT-296: estimate-parity financials (document discount, structured deposit,
  // membership, totals). Mirrors buildEstimatePayload.
  discountValue?: number;
  discountType?: '$' | '%';
  discountReason?: string;
  promotionId?: string | null;
  depositValue?: number;
  depositType?: '$' | '%';
  depositRequired?: boolean;
  membershipPlanSnapshot?: { planId: string; name: string; discountPercent: number };
  subtotal?: number;
  taxTotal?: number;
  discountTotal?: number;
  membershipDiscountTotal?: number;
  grandTotal?: number;
  // Field Service IA §02 — PO reference + recurring blackout dates.
  poNumber?: string;
  /** The call this job is being created from, so the job records its origin. */
  sourceCallControlId?: string | null;
  blackoutDates?: string[];
  /** Recurring non-route crew template used when the backend materializes visits. */
  crew?: CreateJobDto['crew'];
  crewId?: string;
  /** Scheduling v2 — recurring route-based days assignment (ISO weekdays 1=Mon..7=Sun). */
  routeMembership?: { technicianId: string; weekdays: number[] };
  routeMemberships?: Array<{ technicianId: string; weekday: number }>;
  /** Recurring All Day uses DAY_FLEXIBLE visits with date-only materialization. */
  recurringVisitGranularity?: RecurringVisitGranularity;
  /** Controls what the optimizer owns after a recurring date is selected. */
  recurringVisitPlacement?: RecurringVisitPlacement;
  /**
   * Scheduling v2 — the office scheduled the series by hand. Its technicians
   * ride inside it, so no crew or route membership is sent alongside.
   */
  recurringManualSchedule?: RecurringManualSchedule;
  // NOT-542 — only dispatchers (`dispatch:reassign`) may send a Business Unit
  // override; `derivedBusinessUnitId` is the unit the job would carry without
  // one (see lib/business-units/derivedBusinessUnit), used to tell a genuine
  // override apart from re-picking the inherited value.
  derivedBusinessUnitId?: string | null;
  // NOT-565 — this job is a callback (return trip for previously completed work).
  // `isCallback` stamps source=RECALL; `callbackSourceJobId` links the original
  // job when known (an unlinked callback is still allowed and still counts).
  isCallback?: boolean;
  callbackSourceJobId?: string | null;
}): CreateJobDto {
  const dto: CreateJobDto = {
    ...(selectedJobTypeId ? { jobTypeId: selectedJobTypeId } : { jobType: selectedJobTypeValue }),
    priority: form.priority,
  };

  if (form.name.trim()) dto.name = form.name.trim();
  if (poNumber?.trim()) dto.poNumber = poNumber.trim();
  // Provenance, not prefill: without it a job created from a call has no way
  // back to the conversation that produced it.
  if (sourceCallControlId?.trim()) dto.sourceCallControlId = sourceCallControlId.trim();

  // NOT-565 — a callback keeps its normal job type; what marks it is its origin.
  // Stamp source=RECALL so reporting can separate rework from fresh opportunities;
  // link the original job when the user picked one.
  if (isCallback) {
    dto.source = 'RECALL';
    if (callbackSourceJobId) dto.sourceJobId = callbackSourceJobId;
  }

  if (form.contactId) dto.contactId = form.contactId;
  // Bill-to is chosen from the contact's catalog; the backend resolves the
  // snapshot. `undefined` lets the backend apply the contact's default entry.
  if (form.billToId !== undefined) dto.billToId = form.billToId;
  if (form.billToContactId && form.billToContactId !== SUB_CONTACT_NONE)
    dto.billToContactId = form.billToContactId;
  if (form.assignedTo.trim()) dto.assignedTo = form.assignedTo.trim();
  if (form.salesRepId) dto.salesRepId = form.salesRepId;
  if (form.pipelineId) dto.pipelineId = form.pipelineId;
  if (form.stageId) dto.stageId = form.stageId;
  // tagIdsAuthoritative = the form's tag picker was seeded from a LOADED
  // job-scope catalog, so its state is the operator's answer: send it
  // verbatim, [] included ("I deselected them all" must survive the trip).
  // Otherwise (catalog still loading, or the user cannot view tags) omit the
  // key unless tags were somehow picked — an absent key is the sentinel the
  // server reads to inherit the job type's stamp, and it also keeps a raw
  // stored id that the picker could not vet away from job creation's strict
  // tag validation.
  if (tagIdsAuthoritative) {
    dto.tagIds = form.tagIds;
  } else if (form.tagIds.length > 0) {
    dto.tagIds = form.tagIds;
  }
  // Job-level equipment links. Deduped because the server rejects a non-unique
  // array (@ArrayUnique); an empty selection omits the key entirely.
  const equipmentSelection = Array.from(new Set((equipmentIds ?? []).filter(Boolean)));
  if (equipmentSelection.length > 0) dto.equipmentIds = equipmentSelection;

  const serviceAddress = buildServiceAddressFromCustomerState(customerState, form);
  if (serviceAddress) dto.serviceAddress = serviceAddress;
  const billingAddress = buildBillingAddressFromCustomerState(customerState);
  if (billingAddress) dto.billingAddress = billingAddress;
  const serviceContact = buildServiceContact(customerState);
  if (serviceContact) dto.serviceContact = serviceContact;

  const notes = form.issueSummary.trim() || form.notes.trim();
  if (notes) dto.notes = notes;
  const scopeOfWork = (form.scopeOfWork ?? '').trim();
  if (scopeOfWork) dto.scopeOfWork = scopeOfWork;
  if (form.instructions.trim()) dto.instructions = form.instructions.trim();
  if (form.internalNotes.trim()) dto.internalNotes = form.internalNotes.trim();
  // Always sent: an omitted selection means "inherit from the job type", which
  // is not the same as the operator having explicitly chosen the default.
  Object.assign(dto, toTermsPayload(form.terms));
  if (attachmentCount && attachmentCount > 0 && pendingAttachmentId) {
    dto.pendingAttachmentId = pendingAttachmentId;
  }

  const jobLineItems = buildJobLineItems(lineItems, pricingMode, {
    dropUnselectedOptional: true,
  });
  if (jobLineItems) dto.lineItems = jobLineItems;

  dto.bookingType = form.bookingType;
  if (form.bookingType === 'RECURRING') {
    dto.recurringConfig = buildRecurringConfig(
      form,
      blackoutDates,
      recurringVisitGranularity,
      recurringVisitPlacement,
      recurringManualSchedule,
    );
    // Method + schedule are PER_VISIT concepts; subscription/prepaid bill by fee.
    if (form.billingMode !== 'SUBSCRIPTION' && form.billingMode !== 'PREPAID') {
      dto.invoicingMethod = form.invoicingMethod;
      dto.invoicingSchedule = form.invoicingSchedule;
    }
    // Scheduling v2 — route-based day assignment for recurring series. A manual
    // series carries its own technicians and never follows a route.
    const usesStandingRouteMembership = form.recurPatternMode !== 'FLEXIBLE';
    if (!recurringManualSchedule) {
      if (usesStandingRouteMembership && routeMemberships?.length) {
        dto.routeMemberships = routeMemberships;
      } else if (usesStandingRouteMembership && routeMembership) {
        dto.routeMembership = routeMembership;
      }
      if (!usesStandingRouteMembership || (!routeMembership && !routeMemberships?.length)) {
        if (crew && crew.length > 0) dto.crew = crew;
        if (crewId) dto.crewId = crewId;
      }
    }
  } else {
    dto.invoicingMode = form.invoicingMode;
  }

  // Auto-invoice-on-completion is only meaningful for one-off Fixed Price; every other
  // mode has its timing owned by the milestone schedule / invoicing schedule / cadence.
  // Coerce to false there so a job-type default of autoInvoice=true can't double-invoice.
  dto.autoInvoiceOnComplete =
    form.bookingType !== 'RECURRING' && form.invoicingMode === 'FIXED_PRICE'
      ? form.autoInvoiceOnComplete
      : false;
  dto.autoCharge = form.autoCharge;
  if (form.autoCharge && form.autoChargePaymentMethodId) {
    dto.autoChargePaymentMethodId = form.autoChargePaymentMethodId;
  }

  // NOT-296: estimate-parity financials. MIRRORS buildEstimatePayload — header
  // discount, pricing mode, structured deposit, totals + membership snapshot.
  const discountAmount = discountTotal ?? 0;
  dto.discountValue = discountValue;
  dto.promotionId = promotionId ?? null;
  if (discountAmount > 0) {
    dto.discountType = discountType === '%' ? 'PERCENT' : 'FLAT';
  }
  if (discountReason.trim()) dto.discountReason = discountReason.trim();
  dto.pricingMode = pricingMode;

  // Structured deposit replaces the old flat depositAmount input. The deposit
  // amount is computed by the calc hook from grandTotal (PERCENT) or value (FLAT).
  const depositAmount =
    depositValue <= 0
      ? 0
      : depositType === '%'
        ? Math.round((grandTotal ?? 0) * (depositValue / 100) * 100) / 100
        : depositValue;
  if (depositRequired || depositValue > 0 || depositAmount > 0) {
    dto.depositAmount = depositAmount > 0 ? depositAmount : 0;
    dto.depositRequired = depositRequired;
    dto.depositType = depositType === '%' ? 'PERCENT' : 'FLAT';
    dto.depositValue = depositValue;
  }

  if (subtotal !== undefined) dto.subtotal = subtotal;
  if (taxTotal !== undefined) dto.taxTotal = taxTotal;
  if (discountTotal !== undefined) dto.discountTotal = discountTotal;
  if (membershipDiscountTotal !== undefined) {
    dto.membershipDiscountTotal = membershipDiscountTotal;
  }
  if (grandTotal !== undefined) dto.grandTotal = grandTotal;
  if (membershipPlanSnapshot) dto.membershipPlanSnapshot = membershipPlanSnapshot;

  const salesAttribution: { createdByRole?: string; leadSource?: string } = {};
  if (form.createdByRole) salesAttribution.createdByRole = form.createdByRole;
  if (form.leadSource.trim()) salesAttribution.leadSource = form.leadSource.trim();
  if (Object.keys(salesAttribution).length > 0) dto.salesAttribution = salesAttribution;
  dto.reviewRequestEnabled = form.reviewRequestEnabled;
  dto.visitCancellationWindowHours = form.visitCancellationWindowHours.trim()
    ? Number(form.visitCancellationWindowHours)
    : null;
  dto.visitCancellationFeeType = form.visitCancellationFeeType;
  dto.visitCancellationFeeAmount =
    form.visitCancellationFeeType === 'NONE' || !form.visitCancellationFeeAmount.trim()
      ? null
      : Number(form.visitCancellationFeeAmount);
  dto.visitDepositRefundable = form.visitDepositRefundable;
  if (form.requiresCustomerApproval) dto.requiresCustomerApproval = true;

  // NOT — job-level policy overrides from the Advanced section. Each field is only
  // sent when the user overrode it (≠ inherit); an all-inherit job omits the key so
  // the backend inherits the JobType's authorization / sign-off config unchanged.
  const workAuthorization: NonNullable<CreateJobDto['workAuthorization']> = {};
  if (form.workAuthorization !== 'inherit') {
    workAuthorization.required = form.workAuthorization === 'required';
  }
  if (form.authorizationLevel !== 'inherit') workAuthorization.level = form.authorizationLevel;
  if (form.authorizationMethod !== 'inherit') workAuthorization.method = form.authorizationMethod;
  const authMaxAmount = Number(form.authorizationMaxAmount);
  if (form.authorizationMaxAmount.trim() && Number.isFinite(authMaxAmount) && authMaxAmount > 0) {
    workAuthorization.maxAmount = authMaxAmount;
  }
  if (Object.keys(workAuthorization).length > 0) dto.workAuthorization = workAuthorization;

  const completionSignoff: NonNullable<CreateJobDto['completionSignoff']> = {};
  if (form.completionSignoff !== 'inherit') {
    completionSignoff.required = form.completionSignoff === 'required';
  }
  if (form.signoffLevel !== 'inherit') completionSignoff.level = form.signoffLevel;
  if (Object.keys(completionSignoff).length > 0) dto.completionSignoff = completionSignoff;

  // NOT-296: inline milestone payment schedule (only for MILESTONE invoicing).
  // The backend resolves the jobId after the job exists, so none is sent here.
  if (form.invoicingMode === 'MILESTONE' && scheduleRules?.length) {
    dto.paymentSchedule = { rules: scheduleRules.map(mapRuleToDto) };
  }

  // NOT-542 — send the Business Unit whenever the operator picked one that
  // differs from the job-type-derived unit; otherwise the job inherits (omit
  // the key).
  //
  // Deliberately UNGATED. `dispatch:reassign` gates REASSIGNING a job that
  // already carries committed attribution — at create time there is none, so
  // choosing the unit is ordinary data entry, the same reasoning the estimate
  // form's `assign` mode already applies. The old picker screen made this exact
  // choice with no permission check; gating it here would take a CSR's ability
  // to file work under the right unit.
  //
  // A conversion draft answers for a unit it did not choose, so it says
  // whichever of three things is true — and `convertToJob()` re-states the
  // estimate's own choice only for the silent case:
  const carried = form.carriedBusinessUnit ?? null;
  if (carried && form.businessUnitId === carried.id) {
    // Left alone. A SELECTION restates itself, so the wire says what the form
    // shows. An OVERRIDE stays silent on purpose: restating `businessUnitId`
    // would demand `dispatch:reassign` from a reviewer who may only be
    // converting their own estimate, and the carry preserves both the unit and
    // its MANUAL_OVERRIDE provenance — which is what keeps a later job-type or
    // address change from re-deriving the unit out from under it.
    if (carried.source === 'SELECTED') dto.selectedBusinessUnitId = carried.id;
  } else if (isBusinessUnitOverride(form.businessUnitId, derivedBusinessUnitId)) {
    dto.businessUnitId = form.businessUnitId;
  } else if (carried) {
    // Moved OFF the carried unit onto nothing explicit — the picker was
    // cleared, or a new job type's own unit filled it, which the form does
    // automatically when that type allows exactly one. Both mean "derive", and
    // both have to be SAID: silence is what `convertToJob()` reads as "the
    // reviewer left the carried unit alone", and it would put that unit back
    // under a form showing another one.
    //
    // Through the selection channel, and null rather than `businessUnitId:
    // null`: that one means "deliberately Unassigned" and needs
    // `dispatch:reassign`, while this derives from the job type — which is what
    // the picker is already showing when it holds the derived unit.
    dto.selectedBusinessUnitId = null;
  }

  return dto;
}

// NOT-542 — a genuine manual override is a non-null unit that differs from the
// derived unit. Re-picking the derived value or "None" is not an override. A
// multi-unit job type has NO derived unit on create (the server resolves one),
// so any explicit pick — including one of its own units — is an override: that
// is how a dispatcher corrects the resolver within the candidate set.
function isBusinessUnitOverride(
  value: string | null,
  derivedBusinessUnitId: string | null,
): value is string {
  return value != null && value !== derivedBusinessUnitId;
}

export function buildUpdateJobDto({
  id,
  form,
  customerState,
  initial,
  selectedJobTypeId,
  selectedJobTypeValue,
  recurringStartDate,
  recurringVisitGranularity,
  recurringVisitPlacement,
  initialRecurringVisitPlacement,
  blackoutDates,
  initialBlackoutDates,
  poNumber,
  initialPoNumber,
  pricingMode = 'STANDARD',
  discountValue = 0,
  discountType = '$',
  discountReason = '',
  promotionId = null,
  depositValue = 0,
  depositType = '$',
  depositRequired = false,
  canOverrideBusinessUnit = false,
  derivedBusinessUnitId = null,
}: {
  id: string;
  form: JobEditFormValues;
  customerState?: JobCustomerPayloadState;
  initial: JobEditFormValues | null;
  selectedJobTypeId?: string;
  selectedJobTypeValue: JobType;
  recurringStartDate?: string;
  recurringVisitGranularity?: RecurringVisitGranularity;
  recurringVisitPlacement?: RecurringVisitPlacement;
  initialRecurringVisitPlacement?: RecurringVisitPlacement;
  // §14 — recurring blackout dates (edited via the RecurringScheduleEditor's
  // picker); `initialBlackoutDates` is the loaded baseline for dirty-gating.
  blackoutDates?: string[];
  initialBlackoutDates?: string[];
  // PO number lives in separate state (not in JobEditFormValues); `initialPoNumber`
  // is the loaded baseline for dirty-gating.
  poNumber?: string;
  initialPoNumber?: string;
  pricingMode?: 'STANDARD' | 'TM_OVERRIDE';
  discountValue?: number;
  discountType?: '$' | '%';
  discountReason?: string;
  promotionId?: string | null;
  depositValue?: number;
  depositType?: '$' | '%';
  depositRequired?: boolean;
  // NOT-542 — see buildCreateJobDto.
  canOverrideBusinessUnit?: boolean;
  derivedBusinessUnitId?: string | null;
}): UpdateJobDto & { id: string } {
  const dirty = <K extends keyof JobEditFormValues>(key: K) =>
    initial ? form[key] !== initial[key] : true;
  const serviceAddress = buildServiceAddressFromCustomerState(customerState, form);
  const billingAddress = buildBillingAddressFromCustomerState(customerState);
  const serviceContact = buildServiceContact(customerState);
  const depositAmount = parseFloat(form.depositAmount);
  const bookingTypeChanged = dirty('bookingType');
  const recurFrequencyChanged = dirty('recurFrequency');
  const recurEndTypeChanged = dirty('recurEndType');
  const recurEndAfterCountChanged = dirty('recurEndAfterCount');
  const recurEndOnDateChanged = dirty('recurEndOnDate');
  const recurIntervalChanged = dirty('recurInterval');
  const recurIntervalUnitChanged = dirty('recurIntervalUnit');
  const recurVisitStartTimeChanged = dirty('recurVisitStartTime');
  const recurVisitEndTimeChanged = dirty('recurVisitEndTime') || dirty('recurVisitEndOverride');
  const recurVisitPatternsChanged =
    JSON.stringify(form.recurVisitPatterns) !== JSON.stringify(initial?.recurVisitPatterns ?? []);
  const recurWeekDaysChanged =
    JSON.stringify(form.recurWeekDays) !== JSON.stringify(initial?.recurWeekDays ?? []);
  const recurCustomDaysChanged =
    JSON.stringify(form.recurCustomDays) !== JSON.stringify(initial?.recurCustomDays ?? []);
  const recurMonthModeChanged = dirty('recurMonthMode');
  const recurMonthDayChanged = dirty('recurMonthDay');
  const recurMonthLastDayChanged = dirty('recurMonthLastDay');
  const recurMonthDayOfWeekChanged = dirty('recurMonthDayOfWeek');
  const recurMonthDayOfWeekOccurrenceChanged = dirty('recurMonthDayOfWeekOccurrence');
  const billingModeChanged =
    dirty('billingMode') ||
    dirty('recurBaseAmount') ||
    dirty('recurCadence') ||
    dirty('recurAnchorDay') ||
    dirty('recurTermMonths') ||
    dirty('recurEscalationType') ||
    dirty('recurEscalationValue') ||
    dirty('recurEscalationRuleId') ||
    dirty('recurProrationPolicy');
  const escalationChanged =
    dirty('recurEscalationType') || dirty('recurEscalationValue') || dirty('recurEscalationRuleId');
  const hadInitialEscalation =
    !!initial?.recurEscalationRuleId ||
    (!!initial?.recurEscalationType && initial.recurEscalationType !== 'NONE');
  const shouldClearEscalation =
    !!initial &&
    hadInitialEscalation &&
    !form.recurEscalationRuleId &&
    (!form.recurEscalationType || form.recurEscalationType === 'NONE');
  // NOT-358 — advanced-recurrence overrides (season/holiday/contract terms) are
  // now editable on an already-configured series: the PATCH path re-derives them
  // onto the RecurrenceRule + Job columns, so they belong in recurringConfigDirty.
  const recurAdvancedChanged =
    dirty('recurSeasonStart') ||
    dirty('recurSeasonEnd') ||
    dirty('recurHolidayPolicy') ||
    dirty('recurWeatherPolicy') ||
    dirty('recurAutoRenew') ||
    dirty('recurPauseBillingPolicy') ||
    dirty('recurCancellationFeePolicy') ||
    dirty('recurMinimumTermMonths') ||
    dirty('recurCancellationFee');
  const blackoutDatesChanged =
    JSON.stringify(blackoutDates ?? []) !== JSON.stringify(initialBlackoutDates ?? []);
  const recurringPlacementChanged =
    recurringVisitPlacement !== undefined &&
    recurringVisitPlacement !== initialRecurringVisitPlacement;
  const poNumberChanged = (poNumber ?? '').trim() !== (initialPoNumber ?? '').trim();
  const tagIdsChanged = JSON.stringify(form.tagIds) !== JSON.stringify(initial?.tagIds ?? []);
  const recurringConfigDirty =
    form.bookingType === 'RECURRING' &&
    (bookingTypeChanged ||
      dirty('recurPatternMode') ||
      dirty('recurVisitsPerCycle') ||
      dirty('recurDayConstraintMode') ||
      dirty('recurAllowedWeekDays') ||
      recurFrequencyChanged ||
      recurEndTypeChanged ||
      recurEndAfterCountChanged ||
      recurEndOnDateChanged ||
      recurIntervalChanged ||
      recurIntervalUnitChanged ||
      recurVisitStartTimeChanged ||
      recurVisitEndTimeChanged ||
      recurVisitPatternsChanged ||
      recurWeekDaysChanged ||
      recurCustomDaysChanged ||
      recurMonthModeChanged ||
      recurMonthDayChanged ||
      recurMonthLastDayChanged ||
      recurMonthDayOfWeekChanged ||
      recurMonthDayOfWeekOccurrenceChanged ||
      recurringPlacementChanged ||
      billingModeChanged ||
      recurAdvancedChanged ||
      blackoutDatesChanged);

  return {
    id,
    name: form.name.trim(),
    // Only send the job type when the operator actually changed it — an edit to
    // an unrelated field shouldn't re-post it and drag every save through
    // job-type resolution. And send the id, not the label: posting only the name
    // used to rewrite Job.jobType while leaving Job.jobTypeId on the previous
    // type, which is how the two drifted apart — checklist auto-attach resolves
    // a job's type from the stored string, so a drifted job inherited the wrong
    // rules.
    ...(dirty('jobType')
      ? selectedJobTypeId
        ? { jobTypeId: selectedJobTypeId }
        : { jobType: selectedJobTypeValue }
      : {}),
    priority: form.priority,
    ...(dirty('assignedTo') ? { assignedTo: form.assignedTo.trim() || null } : {}),
    ...(dirty('salesRepId') ? { salesRepId: form.salesRepId || null } : {}),
    ...(dirty('pipelineId') && form.pipelineId ? { pipelineId: form.pipelineId } : {}),
    ...(dirty('billToId') ? { billToId: form.billToId ?? null } : {}),
    // A catalog bill-to (billToId) and a per-owner person override (billToContactId)
    // are mutually exclusive — the person picker is hidden once a catalog entry is
    // chosen. So when a catalog bill-to is set, always clear the person override
    // rather than letting a stale/hidden one round-trip alongside it.
    ...(form.billToId
      ? { billToContactId: null }
      : dirty('billToContactId')
        ? {
            billToContactId:
              form.billToContactId && form.billToContactId !== SUB_CONTACT_NONE
                ? form.billToContactId
                : null,
          }
        : {}),
    ...(tagIdsChanged ? { tagIds: form.tagIds } : {}),
    ...(dirty('notes') ? { notes: form.notes.trim() } : {}),
    ...(dirty('scopeOfWork') ? { scopeOfWork: (form.scopeOfWork ?? '').trim() } : {}),
    ...(dirty('instructions') ? { instructions: form.instructions.trim() } : {}),
    ...(dirty('internalNotes') ? { internalNotes: form.internalNotes.trim() } : {}),
    ...(dirty('terms') ? toTermsPayload(form.terms) : {}),
    ...(poNumberChanged ? { poNumber: poNumber?.trim() || '' } : {}),
    ...(serviceAddress ? { serviceAddress } : {}),
    ...(billingAddress
      ? { billingAddress }
      : customerState?.initialHadBillingAddress && !keepsStoredBillingAtService(customerState)
        ? { billingAddress: null }
        : {}),
    ...(serviceContact
      ? { serviceContact }
      : customerState?.initialHadServiceContact
        ? { serviceContact: null }
        : {}),
    ...(bookingTypeChanged ? { bookingType: form.bookingType } : {}),
    ...(recurringConfigDirty
      ? {
          recurringConfig: (() => {
            const recurInterval = Number(form.recurInterval);
            const isMonthly = form.recurFrequency === 'MONTHLY';
            const allowedPatternKinds = recurringPatternKindsForForm(form);
            const visitPatterns = form.recurVisitPatterns.filter((pattern) =>
              allowedPatternKinds.includes(pattern.kind ?? 'WEEKDAY'),
            );
            const flexiblePattern = form.recurPatternMode === 'FLEXIBLE';
            const { effectivePlacement, effectiveVisitGranularity, recurringTimeTbd } =
              resolveRecurringPlacementSettings(
                form,
                recurringVisitGranularity,
                recurringVisitPlacement,
              );
            const firstPattern = flexiblePattern ? undefined : visitPatterns[0];
            return {
              frequency: form.recurFrequency,
              patternMode: form.recurPatternMode,
              ...(effectiveVisitGranularity ? { visitGranularity: effectiveVisitGranularity } : {}),
              ...(effectivePlacement ? { placement: effectivePlacement } : {}),
              ...(form.recurPatternMode === 'FLEXIBLE'
                ? {
                    visitsPerCycle: Math.max(1, Number(form.recurVisitsPerCycle) || 1),
                    dayConstraintMode: form.recurDayConstraintMode,
                    allowedWeekDays:
                      form.recurDayConstraintMode === 'ANY' ? [] : form.recurAllowedWeekDays,
                  }
                : {}),
              startDate: recurringStartDate ?? dateToDateString(new Date()),
              ...(!recurringTimeTbd && (firstPattern?.startTime || form.recurVisitStartTime)
                ? { visitStartTime: firstPattern?.startTime ?? form.recurVisitStartTime }
                : {}),
              ...(!recurringTimeTbd &&
              (firstPattern?.endTime || (form.recurVisitEndOverride && form.recurVisitEndTime))
                ? { visitEndTime: firstPattern?.endTime ?? form.recurVisitEndTime }
                : { visitEndTime: null }),
              ...(!flexiblePattern && visitPatterns.length ? { visitPatterns } : {}),
              endType: form.recurEndType,
              ...(form.recurEndType === 'AFTER_COUNT' && form.recurEndAfterCount
                ? { endAfterCount: Number(form.recurEndAfterCount) }
                : {}),
              ...(form.recurEndType === 'ON_DATE' && form.recurEndOnDate
                ? { endOnDate: form.recurEndOnDate }
                : {}),
              ...(recurInterval > 1 ? { interval: recurInterval } : {}),
              ...(!flexiblePattern &&
              form.recurFrequency === 'WEEKLY' &&
              form.recurWeekDays.length > 0
                ? { customDays: form.recurWeekDays }
                : {}),
              ...(form.recurFrequency === 'CUSTOM' ? { intervalUnit: form.recurIntervalUnit } : {}),
              ...(!flexiblePattern &&
              form.recurFrequency === 'CUSTOM' &&
              form.recurCustomDays.length > 0
                ? { customDays: form.recurCustomDays }
                : {}),
              ...(!flexiblePattern &&
              isMonthly &&
              (form.recurMonthMode ?? 'dayOfMonth') === 'dayOfMonth' &&
              (form.recurMonthLastDay || form.recurMonthDay !== null)
                ? {
                    monthMode: 'dayOfMonth' as const,
                    monthDay: form.recurMonthLastDay ? 32 : (form.recurMonthDay as number),
                  }
                : {}),
              ...(!flexiblePattern &&
              isMonthly &&
              form.recurMonthMode === 'dayOfWeek' &&
              form.recurMonthDayOfWeek !== null
                ? {
                    monthMode: 'dayOfWeek' as const,
                    monthDayOfWeek: form.recurMonthDayOfWeek,
                    monthDayOfWeekOccurrence: form.recurMonthDayOfWeekOccurrence,
                  }
                : {}),
              ...buildBillingModeConfig(form),
              ...(escalationChanged || shouldClearEscalation
                ? buildEscalationConfig(form, { clearEscalation: shouldClearEscalation })
                : {}),
              ...(blackoutDates && blackoutDates.length ? { blackoutDates } : {}),
              // NOT-358 — the advanced-recurrence overrides (season/holiday on the
              // RecurrenceRule; auto-renew, pause/cancellation policy, minimum
              // term, cancellation fee, derived endDate on Job columns) are now
              // applied on update: reconfigureRecurringSeriesInTx re-derives them
              // for an already-configured series, so they take effect instead of
              // looking-saved-but-no-op.
              ...buildRecurAdvancedConfig(form),
            };
          })(),
        }
      : {}),
    ...(dirty('invoicingMethod') ? { invoicingMethod: form.invoicingMethod } : {}),
    ...(dirty('invoicingSchedule') ? { invoicingSchedule: form.invoicingSchedule } : {}),
    ...(dirty('invoicingMode') ? { invoicingMode: form.invoicingMode } : {}),
    // Send a coerced value when the toggle OR any mode-defining field changed, so
    // switching to a non-Fixed-Price mode clears a stale autoInvoiceOnComplete=true
    // (timing there is owned by the schedule/cadence, not this flag).
    ...(dirty('autoInvoiceOnComplete') ||
    dirty('invoicingMode') ||
    bookingTypeChanged ||
    billingModeChanged
      ? {
          autoInvoiceOnComplete:
            form.bookingType !== 'RECURRING' && form.invoicingMode === 'FIXED_PRICE'
              ? form.autoInvoiceOnComplete
              : false,
        }
      : {}),
    ...(dirty('autoCharge') ? { autoCharge: form.autoCharge } : {}),
    ...(dirty('visitCancellationWindowHours')
      ? {
          visitCancellationWindowHours: form.visitCancellationWindowHours.trim()
            ? Number(form.visitCancellationWindowHours)
            : null,
        }
      : {}),
    ...(dirty('visitCancellationFeeType')
      ? { visitCancellationFeeType: form.visitCancellationFeeType }
      : {}),
    ...(dirty('visitCancellationFeeAmount') || dirty('visitCancellationFeeType')
      ? {
          visitCancellationFeeAmount:
            form.visitCancellationFeeType === 'NONE' || !form.visitCancellationFeeAmount.trim()
              ? null
              : Number(form.visitCancellationFeeAmount),
        }
      : {}),
    ...(dirty('visitDepositRefundable')
      ? { visitDepositRefundable: form.visitDepositRefundable }
      : {}),
    // A card goes only with auto-charge, as on create. The form preselects the
    // customer's card for when auto-charge is turned on; sent with it off, the
    // save was refused, so no job of a customer with a card could be edited.
    ...(form.autoCharge && dirty('autoChargePaymentMethodId') && form.autoChargePaymentMethodId
      ? { autoChargePaymentMethodId: form.autoChargePaymentMethodId }
      : {}),
    depositAmount: !isNaN(depositAmount) && depositAmount > 0 ? depositAmount : undefined,
    discountValue,
    discountType: discountType === '%' ? 'PERCENT' : 'FLAT',
    discountReason: discountReason.trim(),
    promotionId: promotionId ?? null,
    pricingMode,
    depositRequired,
    depositType: depositType === '%' ? 'PERCENT' : 'FLAT',
    depositValue,
    salesAttribution:
      form.leadSource || form.createdByRole
        ? {
            leadSource: form.leadSource || undefined,
            createdByRole: form.createdByRole || undefined,
          }
        : undefined,
    // NOT-296 — job-level authorization / sign-off overrides on update. Dirty-gated;
    // an all-inherit selection sends an empty object so the backend clears any prior
    // override rather than leaving a stale one behind.
    ...(dirty('workAuthorization') ||
    dirty('authorizationLevel') ||
    dirty('authorizationMethod') ||
    dirty('authorizationMaxAmount')
      ? {
          workAuthorization: ((): NonNullable<UpdateJobDto['workAuthorization']> => {
            const wa: NonNullable<UpdateJobDto['workAuthorization']> = {};
            if (form.workAuthorization !== 'inherit')
              wa.required = form.workAuthorization === 'required';
            if (form.authorizationLevel !== 'inherit') wa.level = form.authorizationLevel;
            if (form.authorizationMethod !== 'inherit') wa.method = form.authorizationMethod;
            const amt = Number(form.authorizationMaxAmount);
            if (form.authorizationMaxAmount.trim() && Number.isFinite(amt) && amt > 0)
              wa.maxAmount = amt;
            return wa;
          })(),
        }
      : {}),
    ...(dirty('completionSignoff') || dirty('signoffLevel')
      ? {
          completionSignoff: ((): NonNullable<UpdateJobDto['completionSignoff']> => {
            const cs: NonNullable<UpdateJobDto['completionSignoff']> = {};
            if (form.completionSignoff !== 'inherit')
              cs.required = form.completionSignoff === 'required';
            if (form.signoffLevel !== 'inherit') cs.level = form.signoffLevel;
            return cs;
          })(),
        }
      : {}),
    // NOT-542 — Business Unit override, dirty-gated. Only dispatchers may change
    // it. A genuine override sends businessUnitId; reverting to the inherited
    // value (or "None") sends an explicit null so the backend re-derives the
    // job-type unit. Unchanged → omit.
    ...(canOverrideBusinessUnit && dirty('businessUnitId')
      ? isBusinessUnitOverride(form.businessUnitId, derivedBusinessUnitId)
        ? { businessUnitId: form.businessUnitId }
        : { businessUnitId: null }
      : {}),
    reviewRequestEnabled: form.reviewRequestEnabled,
  };
}
